import { useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  Home, 
  CreditCard, 
  QrCode, 
  MapPin, 
  Wallet,
  Users,
  BarChart3,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { NavigationItem } from '@/types'

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    labelKey: 'navigation.dashboard',
    icon: 'Home',
    path: '/dashboard'
  },
  {
    id: 'wallet',
    labelKey: 'navigation.wallet',
    icon: 'Wallet',
    path: '/wallet'
  },
  {
    id: 'qr-scanner',
    labelKey: 'navigation.qrScanner',
    icon: 'QrCode',
    path: '/qr-scanner'
  },
  {
    id: 'merchants',
    labelKey: 'navigation.merchants',
    icon: 'MapPin',
    path: '/merchants'
  },
  {
    id: 'contacts',
    labelKey: 'navigation.contacts',
    icon: 'Users',
    path: '/contacts'
  }
]

const iconMap = {
  Home,
  CreditCard,
  QrCode,
  MapPin,
  Wallet,
  Users,
  BarChart3,
  Settings,
  HelpCircle
}

export function BottomNavigation() {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const handleNavigation = (path: string) => {
    navigate(path)
  }

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -120, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 120, behavior: 'smooth' })
    }
  }

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth)
    }
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      checkScrollButtons()
      container.addEventListener('scroll', checkScrollButtons)
      return () => container.removeEventListener('scroll', checkScrollButtons)
    }
  }, [])

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-[1000] bottom-nav safe-area-inset-bottom bg-white border-t border-gray-200">
      {/* Mobile: Full width navigation */}
      <div className="flex w-full justify-around md:hidden py-2">
        {navigationItems.map((item) => {
          const IconComponent = iconMap[item.icon as keyof typeof iconMap]
          const isActive = location.pathname === item.path

          return (
            <motion.button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className="flex flex-col items-center justify-center py-2 px-2 flex-1"
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                className={`relative p-3 rounded-lg ${
                  isActive ? 'bg-primary bg-opacity-10' : ''
                }`}
                animate={{
                  scale: isActive ? 1.1 : 1,
                }}
                transition={{ duration: 0.2 }}
              >
                <IconComponent
                  className={`h-6 w-6 ${
                    isActive ? 'text-primary' : 'text-text-secondary'
                  }`}
                />
                {isActive && (
                  <motion.div
                    className="absolute inset-0 bg-primary bg-opacity-20 rounded-lg"
                    layoutId="activeTab"
                    transition={{ duration: 0.2 }}
                  />
                )}
              </motion.div>
              
              <span
                className={`text-xs mt-1 whitespace-nowrap ${
                  isActive ? 'text-primary font-medium' : 'text-text-secondary'
                }`}
              >
                {t(item.labelKey || '')}
              </span>
            </motion.button>
          )
        })}
      </div>

      {/* Desktop: Scrollable navigation */}
      <div className="hidden md:flex items-center justify-center py-2">
        <div 
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-hide"
        >
          <div className="flex items-center space-x-1 min-w-max px-2">
            {navigationItems.map((item) => {
              const IconComponent = iconMap[item.icon as keyof typeof iconMap]
              const isActive = location.pathname === item.path

              return (
                <motion.button
                  key={item.id}
                  onClick={() => handleNavigation(item.path)}
                  className="flex flex-col items-center justify-center py-2 px-3 min-w-0 flex-shrink-0"
                  whileTap={{ scale: 0.95 }}
                >
                  <motion.div
                    className={`relative p-2 rounded-lg ${
                      isActive ? 'bg-primary bg-opacity-10' : ''
                    }`}
                    animate={{
                      scale: isActive ? 1.1 : 1,
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <IconComponent
                      className={`h-5 w-5 ${
                        isActive ? 'text-primary' : 'text-text-secondary'
                      }`}
                    />
                    {isActive && (
                      <motion.div
                        className="absolute inset-0 bg-primary bg-opacity-20 rounded-lg"
                        layoutId="activeTab"
                        transition={{ duration: 0.2 }}
                      />
                    )}
                  </motion.div>
                  
                  <span
                    className={`text-xs mt-1 whitespace-nowrap ${
                      isActive ? 'text-primary font-medium' : 'text-text-secondary'
                    }`}
                  >
                    {t(item.labelKey || '')}
                  </span>
                </motion.button>
              )
            })}
          </div>
        </div>
      </div>
    </nav>
  )
}




